<script>
  import { onMount, onDestroy } from "svelte";
  import CardStatus from "../components/Cards/CardStatus.svelte";
  import DashboardLoading from "../components/Cards/DashboardLoading.svelte";
  import {
    fetchEgresoCabs,
    fetchEgresoCabsWithExpandedData,
    subscribeToEgresoCabs,
    unsubscribeFromEgresoCabs,
  } from "../controllers/egresoCabController.js";
  import { DASHBOARD_CONFIG, getStatusLabel } from "../config.js";
  import { updateEgresoCab, deleteEscoltasFromEgreso, addEscoltasToEgreso } from "../models/egresoCabModel";
  import { fetchSucursales } from "../controllers/sucursalController.js";
  import { getDefaultDashboardDateRange } from "../lib/guatemalaTime.js";

  let egresoCabData = [];
  let filteredData = [];
  let isLoading = true;
  let isRefreshing = false;
  let isInitialLoad = true;
  let error = null;
  let searchQuery = "";
  let showMobileFilters = false;
  let sucursalOptions = [];
  let selectedSucursal = "";
  
  const { todayStr, startDateStr } = getDefaultDashboardDateRange();

  // Set default date range filter variables
  let startDate = startDateStr;
  let endDate = todayStr;

  // New variable for delivery method filter
  let selectedDeliveryMethod = "";
  let searchDebounce;

  function getDashboardFilters() {
    return {
      startDate,
      endDate,
      deliveryMethod: selectedDeliveryMethod,
      sucursal: selectedSucursal,
      searchQuery,
    };
  }

  const fetchData = async () => {
    if (isInitialLoad) {
      isLoading = true;
    } else {
      isRefreshing = true;
    }
    error = null;
    try {
      const result = await fetchEgresoCabsWithExpandedData(getDashboardFilters());
      if (result) {
        egresoCabData = result.egresoCab;
        filteredData = result.egresoCab;
      }
    } catch (err) {
      error = "Error al cargar los datos. Inténtelo de nuevo más tarde.";
      console.error("Error fetching data:", err);
    } finally {
      isLoading = false;
      isRefreshing = false;
      isInitialLoad = false;
    }
  };

  const scheduleFetchData = (delay = 0) => {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => {
      fetchData();
    }, delay);
  };

  const groupByStatus = (data) => {
    const grouped = {};
    
    // Initialize all configured statuses
    DASHBOARD_CONFIG.displayStatuses.forEach(status => {
      grouped[status] = [];
    });

    data.forEach((item) => {
      if (grouped[item.sta]) {
        grouped[item.sta].push(item);
      }
    });

    return grouped;
  };

  $: groupedData = groupByStatus(filteredData);
  $: visibleStatuses = DASHBOARD_CONFIG.displayStatuses;

  // Load sucursales on mount
  async function loadSucursales() {
    try {
      const fetchedSucursales = await fetchSucursales();
      if (Array.isArray(fetchedSucursales.sucursal)) {
        sucursalOptions = fetchedSucursales.sucursal.map(e => ({
          key: e.id,
          value: e.descrip
        }));
      }
    } catch (error) {
      console.error('Error al cargar las sucursales:', error);
    }
  }

  const syncFilteredItem = (itemId, updater) => {
    egresoCabData = egresoCabData.map((item) =>
      item.id === itemId ? updater(item) : item
    );
    filteredData = filteredData.map((item) =>
      item.id === itemId ? updater(item) : item
    );
  };

  const handleSearchInput = (event) => {
    searchQuery = event.target.value;
    scheduleFetchData(400);
  };
  
  const handleStartDateChange = (event) => {
    startDate = event.target.value;
    scheduleFetchData();
  };
  
  const handleEndDateChange = (event) => {
    endDate = event.target.value;
    scheduleFetchData();
  };

  const handleDeliveryMethodChange = (event) => {
    selectedDeliveryMethod = event.target.value;
    scheduleFetchData();
  };

  const handleSucursalChange = (event) => {
    selectedSucursal = event.target.value;
    scheduleFetchData();
  };
  
  const clearFilters = () => {
    searchQuery = "";
    startDate = startDateStr;
    endDate = todayStr;
    selectedDeliveryMethod = "";
    selectedSucursal = "";
    scheduleFetchData();
  };

  async function handleLocationUpdate(event) {
    console.log("Event payload in Dashboard (Location Update):", event.detail);
    const { id, geo_target_ref, geo_delivery_ref } = event.detail;

    try {
      // Update in PocketBase
      await updateEgresoCab(id, {
        ...(geo_target_ref && { geo_target_ref }),
        ...(geo_delivery_ref && { geo_delivery_ref })
      });

      syncFilteredItem(id, (item) => ({
        ...item,
        ...(geo_target_ref && { geo_target_ref }),
        ...(geo_delivery_ref && { geo_delivery_ref })
      }));
    } catch (err) {
      console.error("Error updating location:", err);
      error = "Error al actualizar la ubicación. Inténtelo de nuevo más tarde.";
    }
  }

  async function handleEgresoEscoltaUpdate(event) {
    console.log("Event payload in Dashboard (Escoltas Update):", event.detail);
    const { egresoId, escoltas } = event.detail;

    try {
      // Step 1: Delete any existing escoltas for this egreso
      await deleteEscoltasFromEgreso(egresoId);

      // Step 2: Add new escoltas from the payload
      if (escoltas && escoltas.length > 0) {
        await addEscoltasToEgreso(egresoId, escoltas);
      }

      syncFilteredItem(egresoId, (item) => ({
        ...item,
        escoltas: escoltas || []
      }));
    } catch (err) {
      console.error("Error updating escoltas:", err);
      error = "Error al actualizar las escoltas. Inténtelo de nuevo más tarde.";
    }
  }

  async function handleStatusChange(event) {
    console.log("Event payload in Dashboard (Status Change):", event.detail);
    const { itemId, oldStatus, newStatus, item } = event.detail;

    try {
      // Update the item status in PocketBase
      await updateEgresoCab(itemId, { sta: newStatus });

      syncFilteredItem(itemId, (item) => ({
        ...item,
        sta: newStatus
      }));
      
      console.log(`Item ${itemId} status changed from ${oldStatus} to ${newStatus}`);
    } catch (err) {
      console.error("Error updating status:", err);
      error = "Error al actualizar el estado. Inténtelo de nuevo más tarde.";
    }
  }

  onMount(() => {
    fetchData();
    loadSucursales();
    subscribeToEgresoCabs(fetchData);
  });

  onDestroy(() => {
    clearTimeout(searchDebounce);
    unsubscribeFromEgresoCabs();
  });
</script>

<div class="dashboard-toolbar relative md:pt-6 pb-4 flex flex-col md:flex-row justify-between items-start md:items-center">
  <div class="flex items-center gap-3 mb-4 md:mb-0">
    <h2 class="text-white text-lg font-bold">Estado de Pedidos</h2>
  </div>
  
  <!-- Mobile Filter Button -->
  <button
    class="md:hidden px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50 mb-4 flex items-center gap-2 shadow-md"
    on:click={() => showMobileFilters = true}
  >
    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clip-rule="evenodd" />
    </svg>
    Filtros
  </button>

  <!-- Desktop Filters -->
  <div class="hidden md:flex flex-col md:flex-row items-start md:items-center w-full md:w-auto gap-4">
    <div class="flex flex-col md:flex-row items-start md:items-center gap-4">
      <div class="flex items-center gap-2">
        <span class="text-white">Desde:</span>
        <input
          type="date"
          bind:value={startDate}
          on:change={handleStartDateChange}
          max={endDate || todayStr}
          class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div class="flex items-center gap-2">
        <span class="text-white">Hasta:</span>
        <input
          type="date"
          bind:value={endDate}
          on:change={handleEndDateChange}
          min={startDate}
          class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div class="flex items-center gap-2">
        <span class="text-white">Método:</span>
        <select
          bind:value={selectedDeliveryMethod}
          on:change={handleDeliveryMethodChange}
          class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos</option>
          <option value="Despacho">Despacho</option>
          <option value="Repartidor">Repartidor</option>
          <option value="Transporte">Transporte</option>
        </select>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-white">Sucursal:</span>
        <select
          bind:value={selectedSucursal}
          on:change={handleSucursalChange}
          class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todas</option>
          {#each sucursalOptions as option}
            <option value={option.key}>{option.value}</option>
          {/each}
        </select>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <input
        type="text"
        bind:value={searchQuery}
        on:input={handleSearchInput}
        placeholder="Buscar ..."
        class="px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <button 
        on:click={clearFilters}
        class="px-4 py-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 focus:outline-none"
      >
        Limpiar
      </button>
    </div>
  </div>

  <!-- Mobile Search -->
  <div class="md:hidden flex items-center gap-2 w-full">
    <input
      type="text"
      bind:value={searchQuery}
      on:input={handleSearchInput}
      placeholder="Buscar ..."
      class="flex-1 px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
    <button 
      on:click={clearFilters}
      class="px-4 py-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 focus:outline-none"
    >
      Limpiar
    </button>
  </div>
</div>

<!-- Mobile Filter Modal -->
{#if showMobileFilters}
  <div class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div class="bg-white rounded-lg p-6 w-full max-w-md">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold">Filtros</h3>
        <button 
          on:click={() => showMobileFilters = false}
          class="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>
      
      <div class="space-y-4">
        <div class="flex flex-col gap-2">
          <span class="text-gray-700">Desde:</span>
          <input
            type="date"
            bind:value={startDate}
            on:change={handleStartDateChange}
            max={endDate || todayStr}
            class="w-full px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div class="flex flex-col gap-2">
          <span class="text-gray-700">Hasta:</span>
          <input
            type="date"
            bind:value={endDate}
            on:change={handleEndDateChange}
            min={startDate}
            class="w-full px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div class="flex flex-col gap-2">
          <span class="text-gray-700">Método de entrega:</span>
          <select
            bind:value={selectedDeliveryMethod}
            on:change={handleDeliveryMethodChange}
            class="w-full px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos</option>
            <option value="Despacho">Despacho</option>
            <option value="Repartidor">Repartidor</option>
            <option value="Transporte">Transporte</option>
          </select>
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-gray-700">Sucursal:</span>
          <select
            bind:value={selectedSucursal}
            on:change={handleSucursalChange}
            class="w-full px-3 py-1.5 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todas</option>
            {#each sucursalOptions as option}
              <option value={option.key}>{option.value}</option>
            {/each}
          </select>
        </div>
        
        <div class="flex justify-end gap-2 mt-4">
          <button 
            on:click={clearFilters}
            class="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none"
          >
            Limpiar
          </button>
          <button 
            on:click={() => showMobileFilters = false}
            class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<div class="p-4">
  {#if isLoading}
    <DashboardLoading />
  {:else if error}
    <p class="text-red-500">{error}</p>
  {:else}
    {#each Array(Math.ceil(visibleStatuses.length / 3)) as _, rowIndex}
      <div class="flex flex-wrap {rowIndex > 0 ? 'mt-4' : ''}">
        {#each visibleStatuses.slice(rowIndex * 3, (rowIndex + 1) * 3) as status}
          <div class="w-full xl:w-4/12 mb-12 xl:mb-0 px-4">
            <CardStatus
              title={getStatusLabel(status)}
              status={status}
              items={groupedData[status]}
              on:updateLocation={handleLocationUpdate}
              on:updateEgresoEscolta={status === 'ASIGNACION_MESA' ? handleEgresoEscoltaUpdate : undefined}
              on:statusChange={handleStatusChange}
            />
          </div>
        {/each}
      </div>
    {/each}
  {/if}
</div>